import { Component, effect, inject, OnDestroy, OnInit } from "@angular/core";
import { RouterOutlet } from "@angular/router";
import { HeaderComponent } from "../layouts/main/components/header/header.component";
import { interval, startWith, Subject } from "rxjs";
import { WebsocketService } from "../../../../shared/src/lib/services/websocket.service";
import { LiveUpdateService } from "../../../../shared/src/lib/services/live-update.service";
import { ApiService } from "../../../../shared/src";
import { CoreService } from "../../../../shared/src/lib/services/core.service";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { BetPlacementService } from "../../../../shared/src/lib/services/bet-placement.service";
import { DomainService } from "../../../../shared/src/lib/services/domain.service";
import { BalanceService } from "../../../../shared/src/lib/services/balance.service";
import { ExRateService } from "../../../../shared/src/lib/services/ex-rate.service";
import { MyMarketsService } from "./content/my-markets/my-markets.service";
import { NextEventsService } from "./content/home/next-horse-race/next-events.service";
import { MarketOpsService } from "./content/market-layout/market/market-ops.service";
import { SessionTimeoutService } from "../../../../shared/src/lib/services/session-timeout.service";
import { WhiteLabelConfigService } from "../../../../shared/src/lib/services/white-label-config.service";
import { takeUntil, tap } from "rxjs/operators";
import { MarketService } from "../../../../shared/src/lib/services/market.service";
import { BetConfigService } from "../../../../shared/src/lib/services/bet-config.service";

@Component({
  selector: "app-pages",
  imports: [RouterOutlet, HeaderComponent, MatProgressBarModule],
  templateUrl: "./pages.component.html",
  styleUrl: "./pages.component.scss",
})
export class PagesComponent implements OnInit, OnDestroy {
  readonly destroy$ = new Subject<void>();
  wsService = inject(WebsocketService);
  liveUpdateService = inject(LiveUpdateService);
  apiService = inject(ApiService);
  coreService = inject(CoreService);
  placementService = inject(BetPlacementService);
  domainService = inject(DomainService);
  balanceService = inject(BalanceService);
  exRateService = inject(ExRateService);
  myMarketsService = inject(MyMarketsService);
  nextEventsService = inject(NextEventsService);
  marketOps = inject(MarketOpsService);
  wlConfigService = inject(WhiteLabelConfigService);
  sessionTimeoutService = inject(SessionTimeoutService);
  marketService = inject(MarketService);
  betConfigService = inject(BetConfigService);

  constructor() {
    effect(() => {
      const data = this.liveUpdateService._getData(["wl_betslip", "logout", "bet-config"]);
      if (data) {
        if (data?.type === "wl_betslip") {
          this.handleSlipUpdate(data);
        } else if (data?.type === "logout") {
          this.handleLogout(data);
        } else if (data?.type === "bet-config") {
          this.handleBetConfigUpdate(data);
        }
      }
    });
  }

  ngOnInit() {
    this.initConnection();
    this.getConnectionStatus();
    this.getCurrency();
    interval(20000)
      .pipe(
        startWith(0),
        takeUntil(this.destroy$),
        tap(() => this.nextEventsService.triggerFetch()),
      )
      .subscribe();
    this.wlConfigService.fetchWlMarketConfig();
    this.sessionTimeoutService.setupSessionTimeout();
    this.marketService.fetchSports();
  }

  handleSlipUpdate(update: any) {
    const authToken = this.apiService.getUserToken();
    const data = JSON.parse(update?.data || "{}");
    const updateDomain = data?.domain_name;
    const domain = authToken?.user?.DOMAIN_NAME;

    if (domain !== updateDomain) return;
    let app = this.apiService.getUserToken();
    app = {
      ...app,
      user: {
        ...app?.user,
        WHITE_LABEL_BET_SLIP: JSON.stringify(data?.betslip),
      },
    };

    this.apiService.saveUserToken(app);
    this.placementService.notifyChange("slipChange");
  }

  getCurrency() {
    this.coreService
      .fetchCurrency()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          // console.log(response);
          if (response?.status === "ok" && response?.result?.length > 0) {
            let symbols: Record<string, string> = {};
            response?.result?.forEach((each: any) => {
              symbols[each.CODE] = String(each.SYMBOL)?.trim();
            });
            // console.log(symbols);
            this.exRateService.setSymbols(symbols);
            this.exRateService.updateExRate();
          }
        },
      });
  }

  initConnection() {
    // Check if we already have an active connection
    if (this.wsService.connectionStatus.getValue()) {
      console.log("🟢 WebSocket connection already active in PagesComponent");
      return;
    }

    this.wsService
      .startConnection()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (message) => {
          // Skip processing the initial connection success message (true)
          if (message === true) return;

          this.liveUpdateService.setData(message);
          this.liveUpdateService._setData(message);
        },
        error: (error) => console.log("WebSocket error:", error),
      });
  }

  getConnectionStatus() {
    this.wsService.connectionStatus.pipe(takeUntil(this.destroy$)).subscribe({
      next: (status) => {
        this.liveUpdateService.setConnectionStatus(status);
      },
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private handleLogout(payload: any): void {
    console.log("Logout", payload);
    const logoutDomainId = payload?.data?.DOMAIN_ID;

    // Always logout if event has no domain id
    if (!logoutDomainId) {
      this.apiService.handleSignOut();
      return;
    }

    const userDomainId = this.apiService.getUserToken()?.user?.DOMAIN_ID;

    if (userDomainId && String(logoutDomainId) === String(userDomainId)) {
      this.apiService.handleSignOut();
    }
  }

  private handleBetConfigUpdate(payload: any): void {
    console.log("Handling bet-config update:", payload);
    const data = payload?.data?.BET_CONFIG;
    if (data) this.betConfigService.updateBetConfig(data);
  }
}
